/* eslint-disable no-console */
/**
 * TypeScript service worker registration helper for Workbox-generated service-worker.js
 *
 * Usage:
 * import * as serviceWorkerRegistration from './serviceWorkerRegistration';
 * serviceWorkerRegistration.register({
 *   onUpdate: registration => { // show UI asking user to refresh
 *     // e.g. show snackbar with "Refresh" button that calls serviceWorkerRegistration.reloadPage();
 *   },
 *   onSuccess: registration => { console.log('SW registered', registration); }
 * });
 */

type Config = {
  onUpdate?: (registration: ServiceWorkerRegistration) => void
  onSuccess?: (registration: ServiceWorkerRegistration) => void
}

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    window.location.hostname === '[::1]' ||
    // 127.0.0.1/8
    /^127(?:\.(?:25[0-5]|2[0-4]\d|[01]?\d?\d)){3}$/.test(window.location.hostname),
)

/**
 * Register service worker (only in production).
 */
export function register(config?: Config): void {
  if (process.env.NODE_ENV !== 'production') {
    return
  }

  if (!('serviceWorker' in navigator)) {
    return
  }

  const publicUrl: string = (process as any).env?.PUBLIC_URL ?? ''
  const swUrl = `${publicUrl}/service-worker.js`

  window.addEventListener('load', () => {
    if (isLocalhost) {
      // On localhost, check if a service worker exists and is valid.
      checkValidServiceWorker(swUrl, config)

      navigator.serviceWorker.ready
        .then(() => {
          console.log('This app is being served by a service worker (localhost).')
        })
        .catch(() => {
          // ignore
        })
    } else {
      // Normal production environment: register SW
      registerValidSW(swUrl, config)
    }
  })
}

/**
 * Unregister service worker
 */
export function unregister(): Promise<void> {
  if (!('serviceWorker' in navigator)) {
    return Promise.resolve()
  }

  return navigator.serviceWorker.ready
    .then(registration => registration.unregister())
    .then(() => void 0)
    .catch(error => {
      console.error(error)
    })
}

/**
 * If a new SW is waiting, postMessage it with SKIP_WAITING so it can activate immediately.
 * This is optional: Workbox generateSW can be configured with skipWaiting:true to auto-activate.
 * But in many UX flows you want to prompt the user and then call this to activate.
 */
export function skipWaitingAndReload(registration: ServiceWorkerRegistration): void {
  const waitingWorker = registration.waiting
  if (!waitingWorker) {
    // no waiting worker — nothing to do
    return
  }

  // Send a message to the waiting worker to skipWaiting.
  try {
    waitingWorker.postMessage({ type: 'SKIP_WAITING' })
  } catch (err) {
    // Some browsers may throw; still try to call skipWaiting() via registration
  }

  // After instructing the SW to skipWaiting, reload when it takes control.
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    // controllerchange fires when the new SW has taken control -> reload to get fresh content
    window.location.reload()
  })
}

export function reloadPage() {
  window.location.reload()
}

/* ---------- internal helpers ---------- */

function registerValidSW(swUrl: string, config?: Config): void {
  navigator.serviceWorker
    .register(swUrl)
    .then(registration => {
      // Look for updates
      registration.onupdatefound = () => {
        const installingWorker = registration.installing
        if (!installingWorker) {
          return
        }
        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              // New content available — call onUpdate
              console.log('New content is available; please refresh.')

              if (config && config.onUpdate) {
                config.onUpdate(registration)
              }
            } else {
              // Content cached for offline use — call onSuccess
              console.log('Content is cached for offline use.')

              if (config && config.onSuccess) {
                config.onSuccess(registration)
              }
            }
          }
        }
      }
    })
    .catch(error => {
      console.error('Error during service worker registration:', error)
    })
}

function checkValidServiceWorker(swUrl: string, config?: Config): void {
  // Fetch the service worker to see if it exists. If it doesn't, reload the page.
  fetch(swUrl, { headers: { 'Service-Worker': 'script' } })
    .then(response => {
      const contentType = response.headers.get('content-type')
      if (response.status === 404 || (contentType && contentType.indexOf('javascript') === -1)) {
        // Service worker not found. Unregister the current SW and reload.
        navigator.serviceWorker.ready
          .then(registration => registration.unregister())
          .then(() => {
            window.location.reload()
          })
          .catch(() => {
            // ignore
          })
      } else {
        // Service worker found. Proceed with normal registration.
        registerValidSW(swUrl, config)
      }
    })
    .catch(() => {
      console.log('No internet connection found. App is running in offline mode.')
    })
}
